import React, { type ReactNode } from 'react';

import { useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import Form, { ErrorMessage, Field, RequiredAsterisk } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { CloseButton } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { type User as UserPickerUser } from '@atlaskit/user-picker';
import { RecaptchaTermsAndPolicy } from '@atlassian/google-recaptcha';

import { type InvitedUser } from '../../types/user';
import { VOID_FUNC } from '../../utils/constants';
import { CreateContainers } from '../CreateContainer';
import { messages } from '../i18n';
import NoAccessErrorState from '../TeamCreateDialog/NoAccessErrorState';
import TeamTypePicker from '../TeamTypePicker';
import { TeamTypeSelect } from '../TeamTypeSelect/TeamTypeSelect';

import MemberPicker from './MemberPicker';
import { SuggestedTeamMembers } from './TeamMemberSuggestions';
import TeamNameInput from './TeamNameInput';
import { type FormProps } from './types';

const headerStyles = xcss({ marginBottom: 'space.150' });
const footerStyles = xcss({ marginTop: 'space.200' });
const recaptchaWrapperStyles = xcss({ marginTop: 'space.150' });
const fieldStyles = xcss({ gap: 'space.0' });

function EmptyWrapper({ children }: { children: ReactNode }) {
	return <>{children}</>;
}

function DefaultHeader({ children }: { children: ReactNode }) {
	return <Box xcss={headerStyles}>{children}</Box>;
}

function DefaultFooter({ children }: { children: ReactNode }) {
	return (
		<Inline alignInline="end" space="space.100" xcss={footerStyles}>
			{children}
		</Inline>
	);
}

function FormWrapper(props: FormProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const {
		handleSubmit,
		onClose = VOID_FUNC,
		isFetching,
		canCreateTeams,
		showTeamTypePicker,
		showTeamTypeSelect,
		onGoBack,
		teamNameInputProps,
		memberPickerProps,
		customInputFieldProps,
		teamTypePickerProps,
		teamTypeSelectProps,
		cloudId,
		currentUserId,
		customMessages = {},
		customComponents = {},
		showCreateContainers = false,
		createContainersProps,
		handleSuggestedMemberSelection,
		headless,
	} = props;

	const Header = customComponents?.header || customComponents?.modalHeader || DefaultHeader;
	const Title = customComponents?.title || customComponents?.modalTitle || EmptyWrapper;
	const Body = customComponents?.body || customComponents?.modalBody || EmptyWrapper;
	const Footer = customComponents?.footer || customComponents?.modalFooter || DefaultFooter;

	const customTitle = customMessages?.title || customMessages?.modalTitle;
	const submitButtonLabel =
		customMessages?.submitButtonLabel || formatMessage(messages.submitButtonLabelNext);

	if (!canCreateTeams) {
		return (
			<Body>
				<NoAccessErrorState onAction={onClose} />
			</Body>
		);
	}

	const platformisationFormsEnabled = fg('navx_uc_platformisation_forms_enabled');

	const hideHeader = headless && platformisationFormsEnabled;

	return fg('platform-design_system_team-form_conversion') ? (
		<Form onSubmit={handleSubmit} noValidate>
			{!hideHeader && (
				<Header>
					<Title>
						{fg('enable_close_button_in_team_modals') ? (
							<Inline alignBlock="center" space="space.100" spread="space-between">
								<Inline alignBlock="center" space="space.100">
									{onGoBack ? (
										<IconButton
											spacing="compact"
											icon={(iconProps) => <ArrowLeftIcon {...iconProps} LEGACY_size="small" />}
											label={formatMessage(messages.backButton)}
											onClick={onGoBack}
										/>
									) : null}
									{customTitle ? (
										customTitle
									) : (
										<Inline alignBlock="baseline" space="space.050">
											<TeamsIcon
												LEGACY_size="medium"
												label={formatMessage(messages.modalTitleIconLabel)}
											/>
											<Heading as="h2" size="small">
												{formatMessage(messages.modalTitleNext)}
											</Heading>
										</Inline>
									)}
								</Inline>
								<CloseButton onClick={onClose} />
							</Inline>
						) : (
							<Inline alignBlock="center" space="space.100">
								{onGoBack ? (
									<IconButton
										spacing="compact"
										icon={(iconProps) => <ArrowLeftIcon {...iconProps} LEGACY_size="small" />}
										label={formatMessage(messages.backButton)}
										onClick={onGoBack}
									/>
								) : null}
								{customTitle ? (
									customTitle
								) : (
									<Inline alignBlock="baseline" space="space.050">
										<TeamsIcon
											LEGACY_size="medium"
											label={formatMessage(messages.modalTitleIconLabel)}
										/>
										<Heading as="h2" size="small">
											{formatMessage(messages.modalTitleNext)}
										</Heading>
									</Inline>
								)}
							</Inline>
						)}
					</Title>
				</Header>
			)}
			<Body>
				<Stack
					testId="create-team-content-dialog"
					space="space.100"
					xcss={platformisationFormsEnabled ? fieldStyles : undefined}
				>
					{!hideHeader && (
						<Text size="small" color="color.text.subtlest">
							{formatMessage(messages.requiredFieldSummary)}
							<RequiredAsterisk />
						</Text>
					)}
					{customComponents.customInputField &&
						(() => {
							return <customComponents.customInputField {...customInputFieldProps()} />;
						})()}
					<TeamNameInput {...teamNameInputProps} />
					{customComponents.memberPicker ? (
						<customComponents.memberPicker {...memberPickerProps()} />
					) : (
						<Field<InvitedUser[]>
							name="inviteMembers"
							label={formatMessage(messages.invitationsFieldLabelNext)}
							id="inviteMembers"
							isRequired
							defaultValue={memberPickerProps().initialValues || []}
						>
							{({ fieldProps, error }) => {
								const { showErrorMessage, ...props } = memberPickerProps(error);
								const suggestedMembersCloudId = cloudId;
								const suggestedMembersAccountId = currentUserId;

								const handleSuggestedUserClick = (selectedUser: UserPickerUser) => {
									const currentValue = fieldProps.value || [];

									const isUserAlreadySelected = currentValue.some(
										(user) => user.id === selectedUser.id,
									);
									if (isUserAlreadySelected) {
										return;
									}

									const newInvitedUser: InvitedUser = {
										id: selectedUser.id,
										email: selectedUser.email || '',
										fullName: selectedUser.name,
										nickname: selectedUser.publicName,
										avatarUrl: selectedUser.avatarUrl,
									};

									const newValue = [...currentValue, newInvitedUser];

									if (handleSuggestedMemberSelection) {
										handleSuggestedMemberSelection(newInvitedUser);
									}

									fieldProps.onChange(newValue);
								};

								// Convert InvitedUser[] to UserPickerUser[] for SuggestedTeamMembers
								const userPickerUsers = (fieldProps.value || []).map((user) => ({
									id: user.id,
									name: user.fullName,
									publicName: user.nickname,
									avatarUrl: user.avatarUrl,
									type: 'user' as const,
									email: user.email,
								}));

								const initialUserPickerUsers = (props.initialValues || []).map((user) => ({
									id: user.id,
									name: user.fullName,
									publicName: user.nickname,
									avatarUrl: user.avatarUrl,
									type: 'user' as const,
									email: user.email,
								}));
								return (
									<>
										<MemberPicker
											{...fieldProps}
											{...props}
											value={fieldProps.value}
											ariaLabelledBy={`${fieldProps.id}-label`}
											ariaDescribedBy={`${fieldProps.id}-helper`}
											inputId={fieldProps.id}
											onChange={(users, meta) => {
												const invitedUsers = users.map((user) => ({
													id: user.id,
													email: user.email || '',
													fullName: user.name,
													nickname: user.publicName,
													avatarUrl: user.avatarUrl,
												}));
												fieldProps.onChange(invitedUsers);
												// Also call the original onChange from memberPickerProps
												if (props.onChange) {
													props.onChange(users, meta);
												}
											}}
											initialValues={props.initialValues}
										/>
										{fg('enable_team_suggestions') && (
											<SuggestedTeamMembers
												currentPickedUsers={userPickerUsers}
												onSuggestedUserClick={handleSuggestedUserClick}
												initialValues={initialUserPickerUsers}
												cloudId={suggestedMembersCloudId}
												accountId={suggestedMembersAccountId}
											/>
										)}
										{showErrorMessage ? (
											<ErrorMessage testId="invitation-field-empty-error">
												{formatMessage(messages.invitationsFieldErrorMessageNext)}
											</ErrorMessage>
										) : null}
									</>
								);
							}}
						</Field>
					)}
					{showTeamTypeSelect ? <TeamTypeSelect {...teamTypeSelectProps} /> : null}
					{showTeamTypePicker ? <TeamTypePicker {...teamTypePickerProps} /> : null}
					{showCreateContainers ? <CreateContainers {...createContainersProps} /> : null}
				</Stack>
				{memberPickerProps()?.allowEmailInvites ? (
					<Box xcss={recaptchaWrapperStyles}>
						<Text size="small" color="color.text.subtle">
							<RecaptchaTermsAndPolicy />
						</Text>
					</Box>
				) : null}
			</Body>
			<Footer>
				<Button appearance={fg('jsm_ux_uplift') ? 'subtle' : 'default'} onClick={onClose}>
					{formatMessage(messages.cancelButtonLabel)}
				</Button>
				<Button appearance="primary" isLoading={isFetching} type="submit">
					{submitButtonLabel}
				</Button>
			</Footer>
		</Form>
	) : (
		<Form onSubmit={handleSubmit}>
			{({ formProps }) => (
				<form {...formProps} noValidate>
					{!hideHeader && (
						<Header>
							<Title>
								{fg('enable_close_button_in_team_modals') ? (
									<Inline alignBlock="center" space="space.100" spread="space-between">
										<Inline alignBlock="center" space="space.100">
											{onGoBack ? (
												<IconButton
													spacing="compact"
													icon={(iconProps) => <ArrowLeftIcon {...iconProps} LEGACY_size="small" />}
													label={formatMessage(messages.backButton)}
													onClick={onGoBack}
												/>
											) : null}
											{customTitle ? (
												customTitle
											) : (
												<Inline alignBlock="baseline" space="space.050">
													<TeamsIcon
														LEGACY_size="medium"
														label={formatMessage(messages.modalTitleIconLabel)}
													/>
													<Heading as="h2" size="small">
														{formatMessage(messages.modalTitleNext)}
													</Heading>
												</Inline>
											)}
										</Inline>
										<CloseButton onClick={onClose} />
									</Inline>
								) : (
									<Inline alignBlock="center" space="space.100">
										{onGoBack ? (
											<IconButton
												spacing="compact"
												icon={(iconProps) => <ArrowLeftIcon {...iconProps} LEGACY_size="small" />}
												label={formatMessage(messages.backButton)}
												onClick={onGoBack}
											/>
										) : null}
										{customTitle ? (
											customTitle
										) : (
											<Inline alignBlock="baseline" space="space.050">
												<TeamsIcon
													LEGACY_size="medium"
													label={formatMessage(messages.modalTitleIconLabel)}
												/>
												<Heading as="h2" size="small">
													{formatMessage(messages.modalTitleNext)}
												</Heading>
											</Inline>
										)}
									</Inline>
								)}
							</Title>
						</Header>
					)}
					<Body>
						<Stack
							testId="create-team-content-dialog"
							space="space.100"
							xcss={platformisationFormsEnabled ? fieldStyles : undefined}
						>
							{!hideHeader && (
								<Text size="small" color="color.text.subtlest">
									{formatMessage(messages.requiredFieldSummary)}
									<RequiredAsterisk />
								</Text>
							)}
							{customComponents.customInputField &&
								(() => {
									return <customComponents.customInputField {...customInputFieldProps()} />;
								})()}
							<TeamNameInput {...teamNameInputProps} />
							{customComponents.memberPicker ? (
								<customComponents.memberPicker {...memberPickerProps()} />
							) : (
								<Field<InvitedUser[]>
									name="inviteMembers"
									label={formatMessage(messages.invitationsFieldLabelNext)}
									id="inviteMembers"
									isRequired
									defaultValue={memberPickerProps().initialValues || []}
								>
									{({ fieldProps, error }) => {
										const { showErrorMessage, ...props } = memberPickerProps(error);
										const suggestedMembersCloudId = cloudId;
										const suggestedMembersAccountId = currentUserId;

										// Handle suggested user selection
										const handleSuggestedUserClick = (selectedUser: UserPickerUser) => {
											const currentValue = fieldProps.value || [];

											// Check if user is already selected
											const isUserAlreadySelected = currentValue.some(
												(user) => user.id === selectedUser.id,
											);
											if (isUserAlreadySelected) {
												return;
											}

											// Create new invited user
											const newInvitedUser: InvitedUser = {
												id: selectedUser.id,
												email: selectedUser.email || '',
												fullName: selectedUser.name,
												nickname: selectedUser.publicName,
												avatarUrl: selectedUser.avatarUrl,
											};

											// Merge with existing selection
											const newValue = [...currentValue, newInvitedUser];

											if (handleSuggestedMemberSelection) {
												handleSuggestedMemberSelection(newInvitedUser);
											}

											// Update form field value
											fieldProps.onChange(newValue);
										};

										// Convert InvitedUser[] to UserPickerUser[] for SuggestedTeamMembers
										const userPickerUsers = (fieldProps.value || []).map((user) => ({
											id: user.id,
											name: user.fullName,
											publicName: user.nickname,
											avatarUrl: user.avatarUrl,
											type: 'user' as const,
											email: user.email,
										}));

										// Convert initialValues to UserPickerUser[]
										const initialUserPickerUsers = (props.initialValues || []).map((user) => ({
											id: user.id,
											name: user.fullName,
											publicName: user.nickname,
											avatarUrl: user.avatarUrl,
											type: 'user' as const,
											email: user.email,
										}));
										return (
											<>
												<MemberPicker
													{...fieldProps}
													{...props}
													value={fieldProps.value}
													ariaLabelledBy={`${fieldProps.id}-label`}
													ariaDescribedBy={`${fieldProps.id}-helper`}
													inputId={fieldProps.id}
													onChange={(users, meta) => {
														const invitedUsers = users.map((user) => ({
															id: user.id,
															email: user.email || '',
															fullName: user.name,
															nickname: user.publicName,
															avatarUrl: user.avatarUrl,
														}));
														fieldProps.onChange(invitedUsers);
														// Also call the original onChange from memberPickerProps
														if (props.onChange) {
															props.onChange(users, meta);
														}
													}}
													initialValues={props.initialValues}
												/>
												{fg('enable_team_suggestions') && (
													<SuggestedTeamMembers
														currentPickedUsers={userPickerUsers}
														onSuggestedUserClick={handleSuggestedUserClick}
														initialValues={initialUserPickerUsers}
														cloudId={suggestedMembersCloudId}
														accountId={suggestedMembersAccountId}
													/>
												)}
												{showErrorMessage ? (
													<ErrorMessage testId="invitation-field-empty-error">
														{formatMessage(messages.invitationsFieldErrorMessageNext)}
													</ErrorMessage>
												) : null}
											</>
										);
									}}
								</Field>
							)}
							{showTeamTypeSelect ? <TeamTypeSelect {...teamTypeSelectProps} /> : null}
							{showTeamTypePicker ? <TeamTypePicker {...teamTypePickerProps} /> : null}
							{showCreateContainers ? <CreateContainers {...createContainersProps} /> : null}
						</Stack>
						{memberPickerProps()?.allowEmailInvites ? (
							<Box xcss={recaptchaWrapperStyles}>
								<Text size="small" color="color.text.subtle">
									<RecaptchaTermsAndPolicy />
								</Text>
							</Box>
						) : null}
					</Body>
					<Footer>
						<Button appearance={fg('jsm_ux_uplift') ? 'subtle' : 'default'} onClick={onClose}>
							{formatMessage(messages.cancelButtonLabel)}
						</Button>
						<Button appearance="primary" isLoading={isFetching} type="submit">
							{submitButtonLabel}
						</Button>
					</Footer>
				</form>
			)}
		</Form>
	);
}

export default FormWrapper;
